/**
 * React-query hooks over tab spec files. A tab spec is stored with the saved
 * query endpoints: the file is named `tabspec__<name>`, its body is the JSON
 * document and its sidecar is `{ kind: "tabspec" }`.
 *
 * Two facts about those endpoints shape this file:
 * - Axios parses any response body that is valid JSON, so reading a tab spec
 *   gives the parsed document. `getStructured` keeps only text bodies and reads
 *   every tab spec as an empty script, so files are read with `getQuery`.
 * - The Conductor answers HTTP 200 when the Repos server refuses a request,
 *   with a body of `{"Err msg": "Error <code>: ..."}`; a file that does not
 *   exist reads as "Error 1041". An older server may send the bare text
 *   "Error <code>: ..." (the console reads that form too), which counts as a
 *   refusal as well. `saveStructured` does not look at the answer, so every
 *   write is read back before it counts as saved.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "api/queryKeys"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import {
  isTabSpecFileName,
  TAB_SPEC_FILE_PREFIX,
  TabSpecMeta,
} from "types/savedQuery.types"
import { hashBody } from "utils/queryEnvelope"
import {
  normalizeTabSpecDocument,
  NormalizeResult,
  TabSpecDocument,
} from "../types/tabSpecDocument"

/**
 * Names go into a URL that the saved query API does not encode, so they are
 * kept to letters, digits, space, dash and underscore.
 */
const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,59}$/

/** The reason `name` cannot be used, or null when it can. */
export const validateSpecName = (name: string): string | null => {
  const trimmed = name.trim()
  if (!trimmed) return "Give the tab spec a name."
  if (!NAME_PATTERN.test(trimmed))
    return "Use letters, digits, spaces, dashes and underscores only, 60 characters at most, starting with a letter or digit."
  return null
}

/**
 * Refuses a name `validateSpecName` refuses before anything is sent: the
 * name goes into the request URL as it is, and a name from the address bar
 * has not been through the name dialog.
 */
const assertSpecName = (name: string): void => {
  const problem = validateSpecName(name)
  if (problem) throw new Error(problem)
}

export const fileNameOf = (specName: string): string =>
  `${TAB_SPEC_FILE_PREFIX}${specName.trim()}`
export const specNameOf = (fileName: string): string =>
  fileName.slice(TAB_SPEC_FILE_PREFIX.length)

/** Spec names from the newline-separated file list, sorted, without the prefix. */
export const parseSpecList = (text: unknown): string[] =>
  (typeof text === "string" ? text : "")
    .split("\n")
    .map((n) => n.trim())
    .filter((n) => n && isTabSpecFileName(n))
    .map(specNameOf)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))

export class TabSpecConflictError extends Error {
  constructor() {
    super("This tab spec was changed by someone else since you opened it.")
    this.name = "TabSpecConflictError"
  }
}

export interface LoadedSpec {
  result: NormalizeResult
  /**
   * Hash of the stored file, the one `saveStructured` returns for the same
   * text. `save` compares it with the file's current hash.
   */
  etag: string
}

const TAB_SPEC_META: TabSpecMeta = { kind: "tabspec" }

/** How the Repos server says that a file does not exist. */
const NOT_FOUND = /^Error\s+1041:/i
/** A refusal sent as bare text, in the form the console's `isRepoErrorBody` reads. */
const TEXT_REFUSAL = /^Error\s+\d+:/i

/**
 * The message of a Repos refusal (see the file comment), or null when `body`
 * is not one. A body that arrives as text (axios did not parse it) is a
 * refusal when it starts with "Error <code>:", and is parsed otherwise;
 * other text that is not JSON is never a refusal.
 */
const repoErrorOf = (body: unknown): string | null => {
  let value = body
  if (typeof value === "string") {
    const text = value.trim()
    if (TEXT_REFUSAL.test(text)) return text
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (typeof value !== "object" || value === null) return null
  const msg = (value as Record<string, unknown>)["Err msg"]
  return typeof msg === "string" ? msg.trim() : null
}

type SavedQueriesApi = ReturnType<typeof useSavedQueries>

type StoredFile = { found: false } | { found: true; body: unknown; etag: string }

/**
 * Reads the file of spec `name`; a missing file is `found: false`, any other
 * refusal throws, and so does a name that cannot be used (nothing is sent).
 */
const readFile = async (
  getQuery: SavedQueriesApi["getQuery"],
  projectId: string,
  owner: string,
  name: string
): Promise<StoredFile> => {
  assertSpecName(name)
  const body: unknown = (await getQuery(projectId, owner, fileNameOf(name))).data
  const refused = repoErrorOf(body)
  if (refused !== null) {
    if (NOT_FOUND.test(refused)) return { found: false }
    throw new Error(`Could not read the tab spec "${name.trim()}": ${refused}`)
  }
  // Re-serialising a parsed body gives back the text that was saved.
  const text = typeof body === "string" ? body : JSON.stringify(body)
  return { found: true, body, etag: await hashBody(text, TAB_SPEC_META) }
}

/** The project's spec names; a refused request throws rather than reading as no specs. */
const readNames = async (
  getProjectQueries: SavedQueriesApi["getProjectQueries"],
  projectId: string,
  owner: string
): Promise<string[]> => {
  const body: unknown = (await getProjectQueries(projectId, owner)).data
  const refused = repoErrorOf(body)
  if (refused !== null) throw new Error(`Could not list the tab specs: ${refused}`)
  return parseSpecList(body)
}

export const useTabSpecList = (projectId: string, owner: string, enabled = true) => {
  const { getProjectQueries } = useSavedQueries()
  return useQuery<string[]>({
    queryKey: queryKeys.tabSpecs.list(projectId, owner),
    queryFn: () => readNames(getProjectQueries, projectId, owner),
    enabled: enabled && !!projectId,
    staleTime: 5_000,
    meta: { errorTitle: "Could not load the tab specs" },
  })
}

/**
 * The stored spec, or null when no file has that name ("not found", never an
 * empty spec). A name that cannot be used sends nothing: the query stays
 * disabled.
 */
export const useTabSpecDocument = (
  projectId: string,
  owner: string,
  name: string,
  enabled = true
) => {
  const { getQuery } = useSavedQueries()
  return useQuery<LoadedSpec | null>({
    queryKey: queryKeys.tabSpecs.detail(projectId, owner, name),
    queryFn: async () => {
      const file = await readFile(getQuery, projectId, owner, name)
      if (!file.found) return null
      return { result: normalizeTabSpecDocument(file.body), etag: file.etag }
    },
    enabled: enabled && !!projectId && validateSpecName(name) === null,
    staleTime: Infinity,
    retry: false,
    meta: { errorTitle: "Could not load the tab spec" },
  })
}

export const useTabSpecMutations = (projectId: string, owner: string) => {
  const client = useQueryClient()
  const { getProjectQueries, getQuery, saveStructured, deleteQuery } = useSavedQueries()

  const read = (name: string): Promise<StoredFile> =>
    readFile(getQuery, projectId, owner, name)

  const assertNameFree = async (name: string): Promise<void> => {
    const problem = validateSpecName(name)
    if (problem) throw new Error(problem)
    const taken = (await readNames(getProjectQueries, projectId, owner)).some(
      (n) => n.toLowerCase() === name.trim().toLowerCase()
    )
    if (taken) throw new Error(`A tab spec named "${name.trim()}" already exists.`)
  }

  /** Saves under `name` and reads the file back; only a matching read-back counts as saved. */
  const write = async (name: string, doc: TabSpecDocument): Promise<{ etag: string }> => {
    assertSpecName(name)
    const stamped: TabSpecDocument = {
      ...doc,
      name: name.trim(),
      updatedAt: new Date().toISOString(),
    }
    const saved = await saveStructured(
      projectId,
      owner,
      fileNameOf(name),
      JSON.stringify(stamped),
      TAB_SPEC_META
    )
    const stored = await read(name)
    if (!stored.found || stored.etag !== saved.etag)
      throw new Error(`The tab spec "${name.trim()}" could not be saved.`)
    const loaded: LoadedSpec = { result: { ok: true, document: stamped }, etag: saved.etag }
    client.setQueryData<LoadedSpec | null>(
      queryKeys.tabSpecs.detail(projectId, owner, name.trim()),
      loaded
    )
    await client.invalidateQueries({ queryKey: queryKeys.tabSpecs.list(projectId, owner) })
    return saved
  }

  const save = useMutation({
    mutationFn: async (args: {
      name: string
      doc: TabSpecDocument
      loadedEtag: string | null
    }) => {
      if (args.loadedEtag) {
        const current = await read(args.name)
        // A file deleted since it was opened is written again, keeping the edits.
        if (current.found && current.etag !== args.loadedEtag)
          throw new TabSpecConflictError()
      }
      return write(args.name, args.doc)
    },
  })

  const loadDocument = async (name: string): Promise<TabSpecDocument> => {
    const file = await read(name)
    if (!file.found) throw new Error(`The tab spec "${name}" was not found.`)
    const result = normalizeTabSpecDocument(file.body)
    if (!result.ok) throw new Error(`The tab spec "${name}" could not be read.`)
    return result.document
  }

  /** Deleting a file that is already gone counts as done. A name that cannot be used sends nothing. */
  const removeFile = async (name: string): Promise<void> => {
    assertSpecName(name)
    const res = await deleteQuery(projectId, owner, fileNameOf(name))
    const refused = repoErrorOf(res.data)
    if (refused !== null && !NOT_FOUND.test(refused))
      throw new Error(`The tab spec "${name}" could not be deleted: ${refused}`)
    client.removeQueries({ queryKey: queryKeys.tabSpecs.detail(projectId, owner, name) })
    await client.invalidateQueries({ queryKey: queryKeys.tabSpecs.list(projectId, owner) })
  }

  return {
    save: (name: string, doc: TabSpecDocument, loadedEtag: string | null) =>
      save.mutateAsync({ name, doc, loadedEtag }),
    create: async (name: string, doc: TabSpecDocument) => {
      await assertNameFree(name)
      return write(name, doc)
    },
    remove: removeFile,
    duplicate: async (from: string, to: string): Promise<void> => {
      assertSpecName(from)
      await assertNameFree(to)
      const doc = await loadDocument(from)
      await write(to, { ...doc, approval: { state: "draft" } })
    },
    /** There is no rename endpoint: write under the new name, then remove the old file. */
    rename: async (from: string, to: string): Promise<void> => {
      assertSpecName(from)
      await assertNameFree(to)
      const doc = await loadDocument(from)
      await write(to, doc)
      await removeFile(from)
    },
    isSaving: save.isPending,
  }
}
